import { SelectValueType } from 'src/components/Select';
import { ThreeStateValueType } from 'src/components/ThreeStateCheckbox';
import { ConstantsCodeType } from 'src/types/constantsCodes';
import { ProviderServiceCodeType } from 'src/types/provider';

const keySeparator = '||';

export const SHIPPERS_WITH_COUNTRY_FOLDERS = ['PCKT_EXTERNAL', 'PBH'] as const;
export const COUNTRY_GROUP_OTHER = 'OTHER';

export type ServiceCountsType = { active: number; total: number };

export type CountryServiceGroupType = {
    countryCode: string;
    label: string;
    services: ConstantsCodeType[];
};

export type ShipperServiceGroupType = {
    shipperCode: string;
    shipperLabel: string;
    services: ConstantsCodeType[];
    countryGroups: CountryServiceGroupType[] | null;
};

export const getServiceCodeLabel = (serviceCode: ConstantsCodeType) =>
    `${serviceCode.shipperCode}-${serviceCode.label}`;

export const getServiceCodeFromCode = (serviceCode: ConstantsCodeType) =>
    `${serviceCode.shipperCode}${keySeparator}${serviceCode.value}`;

export const getServiceCodeFromData = (serviceCode: ProviderServiceCodeType) =>
    `${serviceCode.SHIPPER_CODE}${keySeparator}${serviceCode.VALUE}`;

export const getServiceCodeObject = (serviceCodeKey: string, serviceCodes: ConstantsCodeType[]) => {
    const arrKey = serviceCodeKey.split(keySeparator);
    const serviceCode = serviceCodes.find(
        (serviceCodeItem) => serviceCodeItem.shipperCode === arrKey[0] && serviceCodeItem.value === arrKey[1]
    );
    if (serviceCode) {
        return { SHIPPER_CODE: serviceCode.shipperCode, VALUE: serviceCode.value };
    }
    return null;
};

export const hasCountryFolders = (shipperCode: string): boolean =>
    (SHIPPERS_WITH_COUNTRY_FOLDERS as readonly string[]).includes(shipperCode);

export const sortServicesAlphabetically = (services: ConstantsCodeType[]): ConstantsCodeType[] =>
    [...services].sort((a, b) => (a.label || '').localeCompare(b.label || '', undefined, { sensitivity: 'base' }));

export const isServiceSelected = (service: ConstantsCodeType, selectedKeys: SelectValueType[]): boolean =>
    selectedKeys.includes(getServiceCodeFromCode(service));

export const getShipperServiceCounts = (
    services: ConstantsCodeType[],
    selectedKeys: SelectValueType[]
): ServiceCountsType => ({
    total: services.length,
    active: services.filter((service) => isServiceSelected(service, selectedKeys)).length,
});

export const getCountryServiceCounts = (
    countryServices: ConstantsCodeType[],
    selectedKeys: SelectValueType[]
): ServiceCountsType => getShipperServiceCounts(countryServices, selectedKeys);

export const getCountryCheckboxState = (
    countryServices: ConstantsCodeType[],
    selectedKeys: SelectValueType[]
): ThreeStateValueType => {
    if (!countryServices.length) {
        return 0;
    }
    const active = countryServices.filter((service) => isServiceSelected(service, selectedKeys)).length;
    if (active === 0) {
        return 0;
    }
    if (active === countryServices.length) {
        return 1;
    }
    return -1;
};

export const toggleCountrySelection = (
    countryServices: ConstantsCodeType[],
    selectedKeys: SelectValueType[]
): SelectValueType[] => {
    const state = getCountryCheckboxState(countryServices, selectedKeys);
    const keysToToggle = countryServices.map((service) => getServiceCodeFromCode(service));
    const selectedSet = new Set(selectedKeys.map(String));

    if (state === 1) {
        keysToToggle.forEach((key) => selectedSet.delete(key));
    } else {
        keysToToggle.forEach((key) => selectedSet.add(key));
    }

    return Array.from(selectedSet);
};

export const toggleServiceSelection = (
    service: ConstantsCodeType,
    selectedKeys: SelectValueType[]
): SelectValueType[] => {
    const key = getServiceCodeFromCode(service);
    if (selectedKeys.includes(key)) {
        return selectedKeys.filter((selectedKey) => selectedKey !== key);
    }
    return [...selectedKeys, key];
};

export const groupServicesByCountry = (
    services: ConstantsCodeType[],
    countryOptions: ConstantsCodeType[],
    otherLabel: string
): CountryServiceGroupType[] => {
    const countryLabelMap = new Map(countryOptions.map((country) => [String(country.value), country.label]));
    const grouped = new Map<string, ConstantsCodeType[]>();
    const otherServices: ConstantsCodeType[] = [];

    services.forEach((service) => {
        const countries = service.COUNTRY_SERVICE_CODES?.filter(Boolean) ?? [];
        if (!countries.length) {
            otherServices.push(service);
            return;
        }
        countries.forEach((countryCode) => {
            const code = String(countryCode);
            const existing = grouped.get(code) ?? [];
            grouped.set(code, [...existing, service]);
        });
    });

    const groups: CountryServiceGroupType[] = Array.from(grouped.entries())
        .map(([countryCode, countryServices]) => ({
            countryCode,
            label: `${countryCode} - ${countryLabelMap.get(countryCode) ?? countryCode}`,
            services: sortServicesAlphabetically(countryServices),
        }))
        .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));

    if (otherServices.length) {
        groups.push({
            countryCode: COUNTRY_GROUP_OTHER,
            label: otherLabel,
            services: sortServicesAlphabetically(otherServices),
        });
    }

    return groups;
};

type ProviderHistoryServiceCodeItem = {
    diffDelete?: boolean;
    new?: {
        LABEL?: string;
        VALUE?: string;
        SHIPPER_CODE?: string;
    };
    LABEL?: string;
    VALUE?: string;
    SHIPPER_CODE?: string;
};

type ProviderHistoryUpdateData = {
    SERVICE_CODES?: ProviderHistoryServiceCodeItem[];
    [key: string]: unknown;
};

type ProviderHistoryUpdate = {
    data?: ProviderHistoryUpdateData;
    [key: string]: unknown;
};

const normalizeProviderHistoryServiceCodeItem = (
    item: ProviderHistoryServiceCodeItem
): ProviderHistoryServiceCodeItem => {
    if (item.new && typeof item.new === 'object' && !item.diffDelete) {
        return item.new;
    }
    return item;
};

export const normalizeProviderHistoryUpdates = <T extends ProviderHistoryUpdate>(updates: T[]): T[] =>
    updates.map((update) => {
        if (!update.data?.SERVICE_CODES) {
            return update;
        }

        return {
            ...update,
            data: {
                ...update.data,
                SERVICE_CODES: update.data.SERVICE_CODES.map(normalizeProviderHistoryServiceCodeItem),
            },
        };
    });

export const groupServiceCodesByShipper = (
    serviceCodes: ConstantsCodeType[],
    shipperCodes: ConstantsCodeType[],
    countryOptions: ConstantsCodeType[],
    otherLabel: string
): ShipperServiceGroupType[] => {
    const servicesByShipper = new Map<string, ConstantsCodeType[]>();

    serviceCodes.forEach((service) => {
        const shipper = service.shipperCode || '';
        const existing = servicesByShipper.get(shipper) ?? [];
        servicesByShipper.set(shipper, [...existing, service]);
    });

    return shipperCodes
        .filter((shipper) => (servicesByShipper.get(String(shipper.value))?.length ?? 0) > 0)
        .sort((a, b) => (a.label || '').localeCompare(b.label || '', undefined, { sensitivity: 'base' }))
        .map((shipper) => {
            const shipperCode = String(shipper.value);
            const services = sortServicesAlphabetically(servicesByShipper.get(shipperCode) ?? []);

            return {
                shipperCode,
                shipperLabel: shipper.label,
                services,
                countryGroups: hasCountryFolders(shipperCode)
                    ? groupServicesByCountry(services, countryOptions, otherLabel)
                    : null,
            };
        });
};
